'use client';

import { BackLink } from '@ps/ui/navigation/BackLink';
import { ChevronLeft } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelAvatar } from '../common/ChannelAvatar';
import { ChannelHeaderMenu } from './ChannelHeaderMenu';

interface Props {
  channel: CommunityChannel;
  /**
   * When true a back chevron is shown on the left edge — used in the
   * mobile layout so the user can return to the channel list. On
   * desktop the master pane is always visible so the back button is
   * suppressed.
   */
  withBackButton?: boolean;
}

/**
 * Channel header strip — avatar, name, counts, kebab. The kebab
 * opens the channel-info popover (Members / Reports / Bookmarks /
 * Notifications / Channel Type / Required Subscription / Delete).
 */
export function ChannelHeader({ channel, withBackButton }: Props) {
  const { homeHref } = useCommunityContext();

  return (
    <header className="border-line flex h-[68px] shrink-0 items-center gap-3 border-b px-4">
      {withBackButton ? (
        <BackLink
          href={homeHref}
          aria-label="Back to channels"
          className="hover:text-content-1 text-content-2 lg:hidden"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </BackLink>
      ) : null}
      <ChannelAvatar src={channel.icon} name={channel.name} size={40} />
      <div className="min-w-0 flex-1">
        <p className="text-content-1 truncate text-sm font-semibold">
          {channel.name}
        </p>
        <p className="text-content-3 text-xs">
          {channel.messageCount} Posts · {channel.memberCount} Members
        </p>
      </div>
      {/* Always visible for everyone: Channel Info (owner → admin console,
          visitor → read-only) + dev-only Copy Reference. */}
      <ChannelHeaderMenu channel={channel} />
    </header>
  );
}
